// Vehicle payer compliance, compliance log and notes for
// VITE_CREDENTIALING_MOCKS, seeded from the Vehicles design. Fictional data
// only. These endpoints don't exist yet; the existing vehicle ones always
// call the API.
import { format } from 'date-fns';
import {
  ApprovalStatus,
  ComplianceLogEntry,
  NoteAuditEntry,
  PersonnelNote,
  ValidityFlag,
} from '@src/types/personnel';
import {
  CreateVehicleNoteRequest,
  UpdateVehicleNoteRequest,
  VehicleNoteAuditRequest,
  VehiclePayerCompliance,
  VehicleRecordKind,
  VehicleRequirementCompliance,
  VehicleTypeCompliance,
} from '@src/types/vehicle';
import { mockApiError } from '../credentialingContract';

const PAYERS = ['IEHP Health Plan', 'LA Care Health Plan', 'Molina Healthcare'];

const requirement = (
  id: number,
  name: string,
  kind: VehicleRecordKind,
  recordId: number | null,
  approval: ApprovalStatus,
  details: string,
  validity: ValidityFlag | null = null
): VehicleRequirementCompliance => ({
  approval,
  details,
  id,
  kind,
  name,
  // locked while the payer reviews it and once it approved it (ST-D2)
  recordEditable: !['Approved', 'In Review'].includes(approval),
  recordId,
  recordType: name,
  status: approval === 'Approved' ? 'Compliant' : 'Non-Compliant',
  validity,
});

const REGISTRATION = requirement(
  1,
  'Registration',
  'registration',
  1,
  'Approved',
  'Expires 10/31/2026',
  'Expiring Soon'
);
const ANNUAL_INSPECTION = requirement(
  2,
  'Annual Vehicle Inspection',
  'inspection',
  1,
  'Approved',
  'Expires 01/20/2027'
);
const TCP_PERMIT = requirement(
  6,
  'TCP Permit',
  'permit',
  1,
  'Approved',
  'Expires 05/31/2027'
);

const vehicleTypeOf = (
  id: number,
  vehicleType: string,
  levelOfService: string,
  requirements: Array<VehicleRequirementCompliance>,
  details: string
): VehicleTypeCompliance => ({
  details,
  id,
  levelsOfService: [levelOfService],
  requirements,
  status: requirements.every(({ status }) => status === 'Compliant')
    ? 'Compliant'
    : 'Non-Compliant',
  vehicleType,
});

// a wheelchair van, as in the design
const VEHICLE_TYPES: Array<VehicleTypeCompliance> = [
  vehicleTypeOf(
    1,
    'Ambulatory Vehicle',
    'Ambulatory',
    [REGISTRATION, ANNUAL_INSPECTION, TCP_PERMIT],
    'Compliant since 06/21/2026'
  ),
  vehicleTypeOf(
    2,
    'Wheelchair Vehicle',
    'Wheelchair',
    [
      REGISTRATION,
      ANNUAL_INSPECTION,
      requirement(
        3,
        'DOT Inspection',
        'inspection',
        2,
        'Approved',
        'Expires 11/05/2026',
        'Expiring Soon'
      ),
      requirement(
        4,
        'Wheelchair Securement Inspection',
        'inspection',
        3,
        'Approved',
        'Expires 09/12/2027'
      ),
      requirement(
        5,
        'Lift Inspection',
        'inspection',
        4,
        'Pending Review',
        'Sent 10/10/2026'
      ),
      TCP_PERMIT,
      requirement(
        7,
        'ADA Accessibility Certification',
        'permit',
        2,
        'Rejected',
        'Photo of the certificate is blurry'
      ),
      requirement(
        8,
        'Mobility Equipment Certification',
        'permit',
        null,
        'Not added',
        'No record yet'
      ),
    ],
    'Lift inspection pending; ADA certification rejected'
  ),
  {
    details: 'A Wheelchair Van can’t serve Gurney Vehicle templates',
    id: 3,
    levelsOfService: ['Gurney'],
    requirements: [],
    status: 'Not applicable',
    vehicleType: 'Gurney Vehicle',
  },
];

const ALL_LINKED_PAYERS = 'All Linked Payers';

const logEntry = (
  id: number,
  payerName: string,
  startDate: string,
  endDate: string | null,
  entryType: ComplianceLogEntry['entryType'],
  isProjected = false
): ComplianceLogEntry => ({
  endDate,
  entryType,
  id,
  isProjected,
  payerName,
  startDate,
});

// the signed-in mock user, as in the Personnel notes mocks; her notes are
// the editable ones
const ME = { name: 'Ana Rivera', role: 'Contractor Credentialing User' };

type MockNote = PersonnelNote & { audit: Array<NoteAuditEntry> };

const now = () => format(new Date(), 'MM/dd/yyyy HH:mm');

const noteOf = (
  id: number,
  createdAt: string,
  author: typeof ME,
  category: PersonnelNote['category'],
  text: string
): MockNote => ({
  audit: [
    {
      action: 'Created',
      at: createdAt,
      id,
      newValue: text,
      originalValue: null,
      role: author.role,
      userName: author.name,
    },
  ],
  authorName: author.name,
  authorRole: author.role,
  category,
  createdAt,
  id,
  isOwn: author.name === ME.name,
  text,
});

// the note with its new text and the edit on top of its history, the newest
// entry first as the design lists it
const withEdit = (note: MockNote, text: string, at: string): MockNote => ({
  ...note,
  audit: [
    {
      action: 'Edited',
      at,
      id: note.audit.length + 1,
      newValue: text,
      originalValue: note.text,
      role: ME.role,
      userName: ME.name,
    },
    ...note.audit,
  ],
  text,
});

const ADMIN = { name: 'James Okafor', role: 'Contractor User Admin' };

let notes: Array<MockNote> = [
  withEdit(
    noteOf(
      1,
      '10/12/2026 09:14',
      ME,
      'Credentialing Record Modified',
      'Replaced the front photo.'
    ),
    'Replaced the front photo after the bumper repair.',
    '10/12/2026 09:21'
  ),
  noteOf(
    2,
    '10/08/2026 15:40',
    ADMIN,
    'Credentialing Record Note',
    'Lift inspection booked with Braun Service Center for 10/10.'
  ),
  noteOf(
    3,
    '06/20/2026 08:05',
    ADMIN,
    'Credentialing Record Modified',
    'Back from transmission repair; status set to Active.'
  ),
];

const withoutAudit = ({ audit, ...note }: MockNote): PersonnelNote => note;

export const vehicleMocks = {
  // every linked payer shows the design's templates
  payerCompliance: (): Array<VehiclePayerCompliance> =>
    PAYERS.map((payerName, index) => ({
      payerId: index + 1,
      payerName,
      vehicleTypes: VEHICLE_TYPES,
    })),
  // every vehicle shows the design's notes, the newest first
  notes: () => notes.map(withoutAudit),
  createNote: ({ note }: CreateVehicleNoteRequest) => {
    const created = noteOf(
      Math.max(0, ...notes.map(({ id }) => id)) + 1,
      now(),
      ME,
      note.category,
      note.text
    );
    notes = [created, ...notes];
    return withoutAudit(created);
  },
  updateNote: ({ noteId, text }: UpdateVehicleNoteRequest) => {
    const note = notes.find(({ id }) => id === noteId);
    if (!note) throw mockApiError(404, 'Note not found');

    const edited = withEdit(note, text, now());
    notes = notes.map((item) => (item.id === noteId ? edited : item));
    return withoutAudit(edited);
  },
  noteAudit: ({ noteId }: VehicleNoteAuditRequest) =>
    notes.find(({ id }) => id === noteId)?.audit ?? [],
  // the newest first, as the design lists it
  complianceLog: (): Array<ComplianceLogEntry> => [
    logEntry(1, PAYERS[1], '07/01/2026', '10/31/2026', 'Compliancy', true),
    logEntry(2, PAYERS[0], '06/21/2026', '10/31/2026', 'Compliancy', true),
    logEntry(3, ALL_LINKED_PAYERS, '06/20/2026', null, 'Onboarded'),
    logEntry(4, ALL_LINKED_PAYERS, '03/01/2026', '05/28/2026', 'Offboarded'),
    logEntry(5, PAYERS[0], '03/02/2026', '05/28/2026', 'Compliancy'),
    logEntry(6, ALL_LINKED_PAYERS, '03/01/2026', null, 'Onboarded'),
  ],
};
